import React, { useRef, useState } from 'react';
import { Button, Input, List, Modal, Popconfirm, Typography } from 'antd';
import {
  FileAddOutlined,
  FolderOpenOutlined,
  SaveOutlined,
} from '@ant-design/icons';
import { get as idbGet, set as idbSet, del as idbDel } from 'idb-keyval';

// ── Recent-project entry (stored in localStorage, no large data) ─────────────
export interface RecentProjectEntry {
  name: string;
  savedAt: string; // ISO string
}

const RECENT_KEY = 'storyball_recent_projects';

export function getRecentProjects(): RecentProjectEntry[] {
  try {
    return JSON.parse(localStorage.getItem(RECENT_KEY) ?? '[]');
  } catch {
    return [];
  }
}

export function pushRecentProject(entry: RecentProjectEntry) {
  const list = [entry, ...getRecentProjects().filter((r) => r.name !== entry.name)].slice(0, 12);
  localStorage.setItem(RECENT_KEY, JSON.stringify(list));
}

/** Store a FileSystemFileHandle in IndexedDB so recent entries can be re-opened directly. */
export async function storeRecentHandle(name: string, handle: any): Promise<void> {
  try { await idbSet(`handle:${name}`, handle); } catch { /* ignore */ }
}

async function getRecentHandle(name: string): Promise<any | null> {
  try { return await idbGet(`handle:${name}`) ?? null; } catch { return null; }
}

/** Store the most-recently used FileSystemFileHandle so the OS picker can start in that folder. */
export async function storeLastHandle(handle: any): Promise<void> {
  try { await idbSet('lastUsedHandle', handle); } catch { /* ignore */ }
}

export async function getLastHandle(): Promise<any | null> {
  try { return await idbGet('lastUsedHandle') ?? null; } catch { return null; }
}

async function removeRecentHandle(name: string): Promise<void> {
  try { await idbDel(`handle:${name}`); } catch { /* ignore */ }
}

function removeRecentProject(name: string) {
  const list = getRecentProjects().filter((r) => r.name !== name);
  localStorage.setItem(RECENT_KEY, JSON.stringify(list));
  removeRecentHandle(name);
}

// ────────────────────────────────────────────────────────────────────────────
interface ProjectModalProps {
  open: boolean;
  onClose: () => void;
  currentProjectName: string;
  onSave: (name: string) => Promise<void>; // saves a .balls project file
  onLoad: (file: File, handle?: any) => Promise<void>; // parses + restores a .balls file
  onNew: () => void;                     // clears everything
}

const { Text } = Typography;

export default function ProjectModal({
  open,
  onClose,
  currentProjectName,
  onSave,
  onLoad,
  onNew,
}: ProjectModalProps) {
  const [saveName, setSaveName] = useState(currentProjectName || 'Untitled');
  const [loading, setLoading] = useState(false);
  const [recent, setRecent] = useState<RecentProjectEntry[]>(getRecentProjects);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Refresh recent list each time the modal opens.
  React.useEffect(() => {
    if (open) {
      setSaveName(currentProjectName || 'Untitled');
      setRecent(getRecentProjects());
    }
  }, [open, currentProjectName]);

  const handleSave = async () => {
    const name = saveName.trim() || 'Untitled';
    setLoading(true);
    try {
      await onSave(name);
      pushRecentProject({ name, savedAt: new Date().toISOString() });
      setRecent(getRecentProjects());
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLoading(true);
    try {
      await onLoad(file, undefined);
      onClose();
    } finally {
      setLoading(false);
      // Reset so the same file can be re-selected next time.
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleOpenWithPicker = async () => {
    if (typeof (window as any).showOpenFilePicker === 'function') {
      try {
        const lastHandle = await getLastHandle();
        const [fileHandle] = await (window as any).showOpenFilePicker({
          types: [{ description: 'StoryBall Project', accept: { 'application/json': ['.balls'] } }],
          multiple: false,
          startIn: lastHandle ?? undefined,
        });
        storeLastHandle(fileHandle);
        const file: File = await fileHandle.getFile();
        setLoading(true);
        try {
          await onLoad(file, fileHandle);
          onClose();
        } finally {
          setLoading(false);
        }
      } catch (err: any) {
        if (err?.name !== 'AbortError') throw err;
      }
    } else {
      fileInputRef.current?.click();
    }
  };

  const handleOpenRecent = async (name: string) => {
    const handle = await getRecentHandle(name);
    if (handle && typeof handle.queryPermission === 'function') {
      try {
        let perm = await handle.queryPermission({ mode: 'readwrite' });
        if (perm !== 'granted') {
          perm = await handle.requestPermission({ mode: 'readwrite' });
        }
        if (perm === 'granted') {
          const file: File = await handle.getFile();
          setLoading(true);
          try {
            await onLoad(file, handle);
            onClose();
          } finally {
            setLoading(false);
          }
          return;
        }
      } catch { /* fall through to picker */ }
    }
    // No stored handle or permission denied — open picker as fallback
    handleOpenWithPicker();
  };

  const handleRemoveRecent = (name: string) => {
    removeRecentProject(name);
    setRecent(getRecentProjects());
  };

  const formatDate = (iso: string) => {
    try {
      return new Date(iso).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' });
    } catch {
      return iso;
    }
  };

  return (
    <Modal
      title="Projects"
      open={open}
      onCancel={onClose}
      footer={null}
      width={480}
    >
      {/* ── New Project ──────────────────────────────────────────────── */}
      <section style={{ marginBottom: 24 }}>
        <Text strong style={{ display: 'block', marginBottom: 8 }}>New Project</Text>
        <Popconfirm
          title="Start a new project?"
          description="All unsaved work will be lost."
          onConfirm={() => { onNew(); onClose(); }}
          okText="Clear & Start New"
          cancelText="Cancel"
        >
          <Button icon={<FileAddOutlined />} block>
            New Project
          </Button>
        </Popconfirm>
      </section>

      {/* ── Save ─────────────────────────────────────────────────────── */}
      <section style={{ marginBottom: 24 }}>
        <Text strong style={{ display: 'block', marginBottom: 8 }}>Save Project</Text>
        <div style={{ display: 'flex', gap: 8 }}>
          <Input
            value={saveName}
            onChange={(e) => setSaveName(e.target.value)}
            onPressEnter={handleSave}
            placeholder="Project name"
            style={{ flex: 1 }}
          />
          <Button type="primary" icon={<SaveOutlined />} loading={loading} onClick={handleSave}>
            Save
          </Button>
        </div>
        <Text type="secondary" style={{ fontSize: 11 }}>
          Saves a <code>.balls</code> file containing all layers and keyframes.
        </Text>
      </section>

      {/* ── Load ─────────────────────────────────────────────────────── */}
      <section style={{ marginBottom: 24 }}>
        <Text strong style={{ display: 'block', marginBottom: 8 }}>Open Project</Text>
        <Button
          icon={<FolderOpenOutlined />}
          loading={loading}
          block
          onClick={handleOpenWithPicker}
        >
          Open .balls file…
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          accept=".balls,application/json"
          style={{ display: 'none' }}
          onChange={handleFileChange}
        />
      </section>

      {/* ── Recent saves ─────────────────────────────────────────────── */}
      {recent.length > 0 && (
        <section>
          <Text strong style={{ display: 'block', marginBottom: 6 }}>Recent Projects</Text>
          <List
            size="small"
            bordered
            dataSource={recent}
            renderItem={(item) => (
              <List.Item
                style={{ padding: '4px 10px', cursor: 'pointer' }}
                onClick={() => handleOpenRecent(item.name)}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'baseline', gap: 8 }}>
                  <Text style={{ fontSize: 13 }} ellipsis>{item.name}</Text>
                  <Text type="secondary" style={{ fontSize: 11, whiteSpace: 'nowrap', flexShrink: 0 }}>
                    {formatDate(item.savedAt)}
                  </Text>
                </div>
              </List.Item>
            )}
          />
        </section>
      )}
    </Modal>
  );
}
